// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { DnaSyngoviaConnection } from '../interfaces/dna-syngovia-connection';
import { DnaSyngoviaDataConnections } from '../interfaces/dna-syngovia-data-connections';
import {
  DnaSyngoviaGroupNode,
  DnaSyngoviaGroupNodeEType,
} from '../interfaces/dna-syngovia-group-node';
import { DnaSyngoviaNodeType } from '../interfaces/dna-syngovia-node';
import { DnaSyngoviaGroupNodeSelectionModel } from '../model/dna-syngovia-group-node-selection-model';
import { DnaSyngoviaSelectionType } from '../model/dna-syngovia-selection-model';

export type GroupNodeSelectionModelByType = {
  [key: string]: DnaSyngoviaGroupNodeSelectionModel[];
};

@Injectable({
  providedIn: 'root',
})
export class DnaSyngoviaGroupNodeService {
  public newGroupNodeSelectionModelByType(): GroupNodeSelectionModelByType {
    return {
      server: [],
      modality: [],
      digital_product: [],
      client: [],
      dicom_proxy: [],
    };
  }

  public filterGroupNodes(
    groupNodeSelectionModelByType: GroupNodeSelectionModelByType,
    dataConnections: DnaSyngoviaDataConnections,
  ): void {
    Object.keys(groupNodeSelectionModelByType).forEach((key) => {
      this.filterGroupNodesByKey(
        groupNodeSelectionModelByType,
        dataConnections,
        key,
      );
    });
  }

  private filterGroupNodesByKey(
    groupNodeSelectionModelByType: GroupNodeSelectionModelByType,
    dataConnections: DnaSyngoviaDataConnections,
    groupNodeType: string,
  ): void {
    if (DnaSyngoviaGroupNodeEType.SERVER === groupNodeType) {
      groupNodeSelectionModelByType[groupNodeType] = [
        new DnaSyngoviaGroupNodeSelectionModel(dataConnections.serverGroup),
      ];
    } else {
      groupNodeSelectionModelByType[groupNodeType] = dataConnections.connections
        .filter(this.groupNodeTypeFilter(groupNodeType))
        .map(
          (connection) =>
            new DnaSyngoviaGroupNodeSelectionModel(connection.groupNode),
        );
    }
  }

  private groupNodeTypeFilter(
    groupNodeType: string,
  ): (connection: DnaSyngoviaConnection) => boolean {
    return (connection) => connection.groupNode.type === groupNodeType;
  }

  public updateGroupNodeSelectionModel(
    groupNodeSelectionModel: DnaSyngoviaGroupNodeSelectionModel | undefined,
    selectedNodes: DnaSyngoviaNodeType[] | undefined,
  ): void {
    if (groupNodeSelectionModel && selectedNodes && selectedNodes.length > 0) {
      groupNodeSelectionModel.updateSelection(selectedNodes);
    }
  }

  public deselectAllModelNodes(
    groupNodeSelectionModelByType: GroupNodeSelectionModelByType,
  ): void {
    if (groupNodeSelectionModelByType) {
      Object.keys(groupNodeSelectionModelByType).forEach((key) => {
        groupNodeSelectionModelByType[key].forEach((model) =>
          model.deselectAll(),
        );
      });
    }
  }

  public currentGroupNodeSelectionModel(
    groupNodeSelectionModelByType: GroupNodeSelectionModelByType,
    groupNode: DnaSyngoviaGroupNode | undefined,
  ): DnaSyngoviaGroupNodeSelectionModel | undefined {
    let current: DnaSyngoviaGroupNodeSelectionModel | undefined = undefined;

    if (groupNode) {
      current = groupNodeSelectionModelByType[groupNode.type].find(
        (model) => model.groupNode === groupNode,
      );
    }

    return current;
  }

  public currentNodeSelection(
    groupNodeSelectionModel: DnaSyngoviaGroupNodeSelectionModel | undefined,
    node: DnaSyngoviaNodeType | undefined,
  ): DnaSyngoviaSelectionType | undefined {
    let current: DnaSyngoviaSelectionType | undefined = undefined;

    if (groupNodeSelectionModel) {
      current = groupNodeSelectionModel.selection.find(
        (element) => element.value === node,
      );
    }

    return current;
  }
}
